import React, { useState, useEffect } from "react";
import { Col, Container, Row } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { Link, useLocation, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SparkApi from "../../api/SparkApi";
import Select from "react-select";
import { Country, State, City } from "country-state-city";

const WarehouseCreate = (props) => {
  const [validated, setValidated] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const paramId = useParams();
  const [warehouseRecords, setWarehouseRecords] = useState({
    factory_id: location?.state?.factory_id || "",
    name: "",
    address: "",
    description: "",
    city: "",
    state: "",
    country: "India",
    pincode: "",
  });
  const [factoryOptions, setFactoryOptions] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState({
    label: "India",
    value: "IN",
  });
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [disableButton, setDisableButton] = useState(false);

  const countries = Country.getAllCountries().map((country) => ({
    label: country.name,
    value: country.isoCode,
  }));

  const states = selectedCountry
    ? State.getStatesOfCountry(selectedCountry.value).map((state) => ({
      label: state.name,
      value: state.isoCode,
    }))
    : [];

  const cities = selectedState
    ? City.getCitiesOfState(selectedCountry.value, selectedState.value).map(
      (city) => ({
        label: city.name,
        value: city.name,
      })
    )
    : [];

  const handleCountryChange = (selectedOption) => {
    setSelectedCountry(selectedOption);
    setSelectedState(null);
    setSelectedCity(null);
    setWarehouseRecords({
      ...warehouseRecords,
      country: selectedOption.label,
      state: "",
      city: "",
    });
  };

  const handleStateChange = (selectedOption) => {
    setSelectedState(selectedOption);
    setSelectedCity(null);
    setWarehouseRecords({
      ...warehouseRecords,
      state: selectedOption.label,
      city: "",
    });
  };

  const handleCityChange = (selectedOption) => {
    setSelectedCity(selectedOption);
    setWarehouseRecords({ ...warehouseRecords, city: selectedOption.label });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setWarehouseRecords({ ...warehouseRecords, [name]: value });
  };

  useEffect(() => {
    async function fetchAllFactory() {
      try {
        const factoriesData = await SparkApi.fetchActiveFactory();
        const wareHouseRecords = factoriesData?.map((sub) => ({
          value: sub.id,
          label: sub.name,
        }));
        setFactoryOptions(wareHouseRecords); // set factory id and name in useState.
      } catch (error) {
        console.error("Error fetching factoriesData:", error);
      }
    }
    fetchAllFactory();
  }, []);

  useEffect(() => {
    if (paramId?.id) {
      fetchWarehouse();
    }
  }, [paramId?.id]);

  const fetchWarehouse = () => {
    async function initWarehouse() {
      try {
        let result = await SparkApi.fetchWarehouseById(paramId?.id);
        if (result.success) {
          setWarehouseRecords(result.records);
          if (result.records.country) {
            const country = countries.find(
              (c) => c.label === result.records.country
            );
            setSelectedCountry(country);

            if (country && result.records.state) {
              const statesList = State.getStatesOfCountry(country.value).map(
                (state) => ({
                  label: state.name,
                  value: state.isoCode,
                })
              );
              const state = statesList.find(
                (s) => s.label === result.records.state
              );
              setSelectedState(state);

              if (state && result.records.city) {
                const citiesList = City.getCitiesOfState(
                  country.value,
                  state.value
                ).map((city) => ({
                  label: city.name,
                  value: city.name,
                }));
                const city = citiesList.find(
                  (c) => c.label === result.records.city
                );
                setSelectedCity(city);
              }
            }
          }
        } else {
          setWarehouseRecords({});
        }
      } catch (error) {
        toast.error("An unexpected error occurred. Please try again.");
      }
    }
    initWarehouse();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !warehouseRecords.name ||
      warehouseRecords.name.trim() === "" ||
      !warehouseRecords.factory_id
    ) {
      toast.error("Missing required fields");
      return;
    }

    try {
      setDisableButton(true);
      if (warehouseRecords.id) {
        const updateWarehouseByFactory = await SparkApi.saveWarehouse(
          warehouseRecords
        );
      
        if (updateWarehouseByFactory?.success) {
          toast.success("Warehouse updated successfully");
          setTimeout(() => {
            setDisableButton(false);
            setWarehouseRecords({
              factory_id: "",
              name: "",
              address: "",
              description: "",
              city: "",
              state: "",
              country: "",
              pincode: "",
            });
            navigate(`/warehouse/${updateWarehouseByFactory?.updateWarehouse?.id}`);
          }, 900);
        } else {
          toast.error(
            updateWarehouseByFactory?.message ||
            "Failed to update warehouse. Please try again."
          );
        }
      } else {
        const warehouseResult = await SparkApi.createWarehouse(
          warehouseRecords
        );

        if (warehouseResult?.success) {
          toast.success("Warehouse created successfully");
          setTimeout(() => {
            setDisableButton(false);
            setWarehouseRecords({
              factory_id: "",
              name: "",
              address: "",
              description: "",
              city: "",
              state: "",
              country: "",
              pincode: "",
            });
            location.state?.factory_id
              ? navigate(`/factories/${warehouseResult?.data?.factory_id}`)
              : navigate(`/warehouse`);
          }, 900);
        } else {
          toast.error(
            warehouseResult?.message ||
            "Failed to create warehouse. Please try again."
          );
        }
      }
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
    }
  };

  const handleCancel = () => {
    if (warehouseRecords.id) {
      navigate("/warehouse/" + warehouseRecords.id, {
        state: warehouseRecords,
      });
    } else {
      navigate(`/warehouse`);
    }
  };

  return (
    <Container className="view-form">
      <Row>
        <Col></Col>
        <Col lg={8} className="pb-1 pt-2">
          <Link className="nav-link" to="/warehouse">
            Home <i className="fa-solid fa-chevron-right"></i>{" "}
            <div style={{ color: "#23468c", display: "inline" }}>Warehouse</div>
          </Link>
        </Col>
        <Col></Col>
      </Row>
      <Row>
        <Col></Col>
        <Col lg={8}>
          <Form
            className="mt-3"
            onSubmit={handleSubmit}
            noValidate
            validated={validated}
          >
            <Row className="view-form-header align-items-center">
              <Col lg={8}>
                {warehouseRecords.id ? (
                  <h5>Edit Warehouse {warehouseRecords.firstname}</h5>
                ) : (
                  <h5>Create Warehouse</h5>
                )}
              </Col>
              <Col lg={4} className="d-flex justify-content-end">
                <Button disabled={disableButton} className="btn-sm mx-2" onClick={handleSubmit}>
                  Save
                </Button>
                <Button
                  className="btn-sm"
                  variant="danger"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </Col>
            </Row>
            <Row className="ibs-edit-form pb-3 ">
              <Row className="mb-3">
                <Col lg={6}>
                  <Form.Group className="mx-3">
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      required
                      style={{ height: "36px" }}
                      type="text"
                      name="name"
                      placeholder="Enter name"
                      value={warehouseRecords.name}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col lg={6}>
                  <Form.Group className="mx-3" controlId="formBasicAddress">
                    <Form.Label
                      className="form-view-label"
                      htmlFor="formBasicAddress"
                    >
                      Address
                    </Form.Label>
                    <Form.Control
                      style={{ height: "36px" }}
                      type="text"
                      name="address"
                      placeholder="Enter address"
                      value={warehouseRecords.address}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col lg={6}>
                  <Form.Group className="mx-3">
                    <Form.Label>Country</Form.Label>
                    <Select
                      style={{ height: "36px" }}
                      value={selectedCountry}
                      onChange={handleCountryChange}
                      options={countries}
                      placeholder="Select Country"
                    />
                  </Form.Group>
                </Col>
                <Col lg={6}>
                  <Form.Group className="mx-3">
                    <Form.Label>State</Form.Label>
                    <Select
                      style={{ height: "36px" }}
                      value={selectedState}
                      onChange={handleStateChange}
                      options={states}
                      placeholder="Select State"
                      isDisabled={!selectedCountry}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col lg={6}>
                  <Form.Group className="mx-3">
                    <Form.Label>City</Form.Label>
                    <Select
                      style={{ height: "36px" }}
                      value={selectedCity}
                      onChange={handleCityChange}
                      options={cities}
                      placeholder="Select City"
                      isDisabled={!selectedState}
                    />
                  </Form.Group>
                </Col>
                <Col lg={6}>
                  <Form.Group className="mx-3" controlId="formBasicPin">
                    <Form.Label
                      className="form-view-label"
                      htmlFor="formBasicPin"
                    >
                      Pincode
                    </Form.Label>
                    <Form.Control
                      style={{ height: "36px" }}
                      type="text"
                      name="pincode"
                      placeholder="Enter pincode"
                      value={warehouseRecords.pincode}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col lg={6}>
                  {factoryOptions && (
                    <Form.Group className="mx-3">
                      <Form.Label
                        className="form-view-label"
                        htmlFor="formBasicFactory"
                      >
                        Factory name
                      </Form.Label>
                      <Form.Select
                        disabled={
                          location?.state?.factory_id && !warehouseRecords?.id
                            ? true
                            : false
                        }
                        name="factory_id"
                        // value={warehouseRecords.factory_id}
                        value={
                          warehouseRecords.factory_id ||
                          location?.state?.factory_id
                        } // state is a factoryId
                        onChange={handleChange}
                        required
                      >
                        <option disabled selected>
                          --- Select Factory ---
                        </option>
                        {factoryOptions.map((factoryRec) => (
                          <option
                            key={factoryRec.value}
                            value={factoryRec.value}
                          >
                            {factoryRec.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  )}
                </Col>
                <Col lg={6}>
                  <Form.Group className="mx-3" controlId="formBasicDescription">
                    <Form.Label
                      className="form-view-label"
                      htmlFor="formBasicDescription"
                    >
                      Description
                    </Form.Label>
                    <Form.Control
                      style={{ height: "36px" }}
                      as="textarea"
                      name="description"
                      placeholder="Enter description"
                      value={warehouseRecords.description}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Row>
          </Form>
        </Col>
        <Col></Col>
      </Row>

      <ToastContainer
        position="top-center"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </Container>
  );
};

export default WarehouseCreate;
